import { action, observable, computed, makeObservable } from "mobx";
import { MrbBaseViewStore } from "mrb/core";
import {
    canExpandLeft,
    canExpandRight,
    canExpandTop,
    canExpandBottom,
    canCollapseTop,
    canCollapseLeft,
    canCollapseRight,
    canCollapseBottom,
    isGridItemDefined,
} from "administration/naki-mobile/utils";
import { findIndex, filter, some } from "lodash";
import { localizationService } from "common/localization";

const MAX_PAGES = 10;
const MIN_PAGES = 1;

let localPageIdSequence = 0;
function createLocalPageId() {
    localPageIdSequence += 1;
    return `local-page-${localPageIdSequence}`;
}

class BaseFeaturedSectionViewStore extends MrbBaseViewStore {
    rowsLimit = 3;
    columnsLimit = 3;

    // Includes pages added or deleted locally but not yet persisted via `updateFeaturedGrid`
    // (the Save button) — see `pages` below for what's actually shown/counted.
    @observable _pages = [];

    @observable currentPageIndex = 0;

    @observable isSubmitting = false;

    mobileAppSystemType = null;

    constructor(rootStore, { routeStore }) {
        super(rootStore);
        makeObservable(this);
        this.routeStore = routeStore;

        this.editItem = this.editItem.bind(this);
    }

    onInit() {
        return this.getFeaturedPages();
    }

    @computed
    get items() {
        return this.currentPage ? this.currentPage.items : [];
    }

    set items(value) {
        if (this.currentPage) {
            this.currentPage.items = value;
        }
    }

    @computed
    get pages() {
        return filter(this._pages, (page) => !page.isDeleted);
    }

    @computed
    get currentPage() {
        return this.pages[this.currentPageIndex] || null;
    }

    @computed
    get pageCount() {
        return this.pages.length;
    }

    @computed
    get canAddPage() {
        return this.pageCount < MAX_PAGES;
    }

    @computed
    get canDeletePage() {
        return this.pageCount > MIN_PAGES;
    }

    @computed
    get isCurrentPageBlank() {
        if (!this.currentPage) {
            return true;
        }
        return !some(this.currentPage.items, (item) => isGridItemDefined(item));
    }

    isPageBlank(page) {
        return !some(page.items, (item) => isGridItemDefined(item));
    }

    findBlankPageIndex() {
        return findIndex(this.pages, (page) => this.isPageBlank(page));
    }

    @action.bound
    async getFeaturedPages() {
        try {
            const pages = await this.routeStore.getFeaturedPages(this.mobileAppSystemType);
            this.setPages(pages);
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.NAKI_MOBILE.FEATURED_SECTION.PAGES.LOAD_ERROR")
            );
        }
    }

    @action.bound
    setPages(pages, preserveIndex = 0) {
        // The API returns each page's cells under `gridItems`; the rest of this store
        // (inherited from the pre-paging single-grid editor) reads/writes `items`.
        this._pages = pages.map((page) => ({
            ...page,
            items: page.gridItems || page.items || [],
            isNew: false,
            isDeleted: false,
        }));
        this.currentPageIndex = Math.min(Math.max(preserveIndex, 0), Math.max(pages.length - 1, 0));
    }

    @action.bound
    setCurrentPageIndex(index) {
        this.currentPageIndex = index;
    }

    @action.bound
    goToPreviousPage() {
        if (this.pageCount <= 1) {
            return;
        }
        this.setCurrentPageIndex((this.currentPageIndex - 1 + this.pageCount) % this.pageCount);
    }

    @action.bound
    goToNextPage() {
        if (this.pageCount <= 1) {
            return;
        }
        this.setCurrentPageIndex((this.currentPageIndex + 1) % this.pageCount);
    }

    @action.bound
    setIsSubmitting(value) {
        this.isSubmitting = value;
    }

    @action.bound
    addPage() {
        // Local only — nothing is persisted until Save (`updateFeaturedGrid`) is pressed.
        if (!this.canAddPage) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.NAKI_MOBILE.FEATURED_SECTION.PAGES.MAX_PAGES_REACHED")
            );
            return;
        }
        this._pages.push({
            id: createLocalPageId(),
            items: [],
            isNew: true,
            isDeleted: false,
        });
        this.setCurrentPageIndex(this.pages.length - 1);
        this.ensureGridStructure();
    }

    @action.bound
    deletePage() {
        if (!this.canDeletePage || !this.currentPage) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.NAKI_MOBILE.FEATURED_SECTION.PAGES.MIN_PAGES_REACHED")
            );
            return;
        }

        this.rootStore.confirmDialogStore.showConfirm({
            message: localizationService.t("ADMINISTRATION.NAKI_MOBILE.FEATURED_SECTION.PAGES.DELETE_PAGE_CONFIRM_MESSAGE"),
            description: localizationService.t(
                "ADMINISTRATION.NAKI_MOBILE.FEATURED_SECTION.PAGES.DELETE_PAGE_CONFIRM_DESCRIPTION"
            ),
            onConfirm: this.confirmDeletePage,
        });
    }

    @action.bound
    confirmDeletePage() {
        // Local only — a not-yet-saved page is dropped outright; an already-persisted page is
        // just flagged and hidden until Save actually deletes it server-side.
        const page = this.currentPage;
        const deletedIndex = this.currentPageIndex;

        if (page.isNew) {
            this._pages = filter(this._pages, (p) => p.id !== page.id);
        } else {
            page.isDeleted = true;
        }

        this.currentPageIndex = Math.min(deletedIndex, Math.max(this.pages.length - 1, 0));
    }

    @action.bound
    async updateFeaturedGrid() {
        const blankPageIndex = this.findBlankPageIndex();
        if (blankPageIndex !== -1) {
            this.setCurrentPageIndex(blankPageIndex);
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.NAKI_MOBILE.FEATURED_SECTION.PAGES.BLANK_PAGE_ERROR")
            );
            return;
        }
        try {
            this.setIsSubmitting(true);

            // Create/update surviving pages *before* deleting anything: a page being deleted to
            // "make room" for a new one must not leave the system type with zero pages in
            // between, which the API rejects (409 - a system type must always keep at least one).
            for (const page of this.pages) {
                if (page.isNew) {
                    const createdPage = await this.routeStore.createFeaturedPage(this.mobileAppSystemType);
                    if (some(page.items, isGridItemDefined)) {
                        await this.routeStore.updateFeaturedPageGrid(createdPage.id, page.items);
                    }
                } else {
                    await this.routeStore.updateFeaturedPageGrid(page.id, page.items);
                }
            }

            const pagesPendingDelete = filter(this._pages, (page) => page.isDeleted && !page.isNew);
            for (const page of pagesPendingDelete) {
                await this.routeStore.deleteFeaturedPage(page.id);
            }

            const pages = await this.routeStore.getFeaturedPages(this.mobileAppSystemType);
            this.setPages(pages, this.currentPageIndex);

            this.rootStore.notificationStore.success(
                localizationService.t("ADMINISTRATION.NAKI_MOBILE.FEATURED_SECTION.PAGES.SAVE_SUCCESS")
            );
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.NAKI_MOBILE.FEATURED_SECTION.PAGES.SAVE_ERROR")
            );
        } finally {
            this.setIsSubmitting(false);
        }
    }

    gridConflict(rowNum, columnNum) {
        const index = findIndex(
            this.items,
            (item) =>
                rowNum >= item.gridRow &&
                rowNum <= item.gridRow + (item.gridRowSpan - 1) &&
                columnNum >= item.gridColumn &&
                columnNum <= item.gridColumn + (item.gridColumnSpan - 1)
        );
        return index !== -1;
    }

    editItem(item) {
        this.editItemModalParams.open({
            item: item,
        });
    }

    @action.bound
    removeGridElementsInConflict(newGridElement) {
        const itemsWithoutConflict = filter(
            this.items,
            (item) =>
                !(
                    newGridElement.gridColumn <= item.gridColumn + (item.gridColumnSpan - 1) &&
                    newGridElement.gridColumn + (newGridElement.gridColumnSpan - 1) >= item.gridColumn &&
                    newGridElement.gridRow <= item.gridRow + (item.gridRowSpan - 1) &&
                    newGridElement.gridRow + (newGridElement.gridRowSpan - 1) >= item.gridRow
                )
        );

        this.items = itemsWithoutConflict;
    }

    @action.bound
    ensureGridStructure() {
        for (let row = 0; row < this.rowsLimit; row++) {
            for (let column = 0; column < this.columnsLimit; column++) {
                const index = findIndex(
                    this.items,
                    (item) =>
                        row >= item.gridRow &&
                        row <= item.gridRow + (item.gridRowSpan - 1) &&
                        column >= item.gridColumn &&
                        column <= item.gridColumn + (item.gridColumnSpan - 1)
                );
                if (index === -1) {
                    // Item exists
                    this.items.push({
                        gridRow: row,
                        gridColumn: column,
                        gridRowSpan: 1,
                        gridColumnSpan: 1,
                    });
                }
            }
        }
    }

    @action.bound
    expandToLeft(item) {
        if (!canExpandLeft(item.gridColumn)) {
            // Expand not supported
            return;
        }

        const newGridElement = { ...item };

        // Push item to left and increase column span
        --newGridElement.gridColumn;
        newGridElement.gridColumnSpan++;

        //TODO: Find all items that should be moved
        this.removeGridElementsInConflict(newGridElement);
        this.items.push(newGridElement);
        this.ensureGridStructure();
    }

    @action.bound
    collapseLeft(item) {
        if (!canCollapseLeft(item.gridColumnSpan)) {
            // Collapse not supported
            return;
        }

        const newGridElement = { ...item };
        // Increase column span
        newGridElement.gridColumnSpan--;
        newGridElement.gridColumn++;

        //TODO: Find all items that should be moved
        this.removeGridElementsInConflict(newGridElement);
        this.items.push(newGridElement);
        this.ensureGridStructure();
    }

    @action.bound
    expandToRight(item) {
        if (!canExpandRight(item.gridColumn, item.gridColumnSpan, this.columnsLimit)) {
            // Expand not supported
            return;
        }

        const newGridElement = { ...item };

        // Increase column span
        newGridElement.gridColumnSpan++;

        this.removeGridElementsInConflict(newGridElement);
        this.items.push(newGridElement);
        this.ensureGridStructure();
    }

    @action.bound
    collapseRight(item) {
        if (!canCollapseRight(item.gridColumnSpan)) {
            // Collapse not supported
            return;
        }

        const newGridElement = { ...item };
        // Increase column span
        newGridElement.gridColumnSpan--;

        //TODO: Find all items that should be moved
        this.removeGridElementsInConflict(newGridElement);
        this.items.push(newGridElement);
        this.ensureGridStructure();
    }

    @action.bound
    expandToTop(item) {
        if (!canExpandTop(item.gridRow)) {
            // Expand not supported
            return;
        }

        const newGridElement = { ...item };

        // Push item to top and increase row span
        --newGridElement.gridRow;
        newGridElement.gridRowSpan++;

        this.removeGridElementsInConflict(newGridElement);
        this.items.push(newGridElement);
        this.ensureGridStructure();
    }

    @action.bound
    collapseTop(item) {
        if (!canCollapseTop(item.gridRowSpan)) {
            // Collapse not supported
            return;
        }

        const newGridElement = { ...item };
        // Increase column span
        newGridElement.gridRow++;
        newGridElement.gridRowSpan--;

        //TODO: Find all items that should be moved
        this.removeGridElementsInConflict(newGridElement);
        this.items.push(newGridElement);
        this.ensureGridStructure();
    }

    @action.bound
    expandToBottom(item) {
        if (!canExpandBottom(item.gridRow, item.gridRowSpan, this.rowsLimit)) {
            // Expand not supported
            return;
        }

        const newGridElement = { ...item };

        // Increase row span
        newGridElement.gridRowSpan++;

        this.removeGridElementsInConflict(newGridElement);
        this.items.push(newGridElement);
        this.ensureGridStructure();
    }

    @action.bound
    collapseBottom(item) {
        if (!canCollapseBottom(item.gridRowSpan)) {
            // Collapse not supported
            return;
        }

        const newGridElement = { ...item };
        // Increase column span
        newGridElement.gridRowSpan--;

        //TODO: Find all items that should be moved
        this.removeGridElementsInConflict(newGridElement);
        this.items.push(newGridElement);
        this.ensureGridStructure();
    }

    @action.bound
    clearSelection(item) {
        item.stationId = null;
        item.station = null;
        item.podcastId = null;
        item.podcast = null;
        item.nakiNigunArtistId = null;
        item.nakiNigunArtist = null;
        item.nakiNigunAlbumId = null;
        item.nakiNigunAlbum = null;
    }
}

export default BaseFeaturedSectionViewStore;
